import { InstallationOptionsEnum } from '@updater/consumer-graph';
import { addDays, addHours, parse } from 'date-fns';
import {
  AttDisabledInstallationDates,
  CoxDisabledInstallationDates,
  SpectrumDisabledInstallationDates,
  XfinityDisabledInstallationDates,
} from 'flows/tvi/constants/disabled-dates';
import {
  AttInstallationWindows,
  CoxInstallationWindows,
  SpectrumInstallationWindows,
  XfinityInstallationWindows,
} from 'flows/tvi/constants/installation-windows';
import { useUserData } from 'flows/core/hooks/use-profile';
import { useReservationAdapter } from 'flows/tvi/hooks/use-reservation-adapter';

const now = new Date();
const getDateOffsetByHours = (hoursFromNow: number): string => {
  return formatDate(
    addHours(
      new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
      ),
      hoursFromNow
    )
  );
};

const getDateOffsetByDays = (daysFromNow: number): string => {
  return formatDate(
    addDays(
      new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
      ),
      daysFromNow
    )
  );
};

export interface DateRequirements {
  minDate: string;
  maxDate: string;
  disabledDates: string[];
  disabledDaysOfWeek?: number[];
}

export interface ActivationDefaults {
  estimatedDate: string | null;
  dateRequirements: DateRequirements;
  defaultTimeWindow?: string;
  loading: boolean;
}

export const DefaultDateRequirements: DateRequirements = {
  minDate: getDateOffsetByHours(72),
  maxDate: getDateOffsetByDays(29),
  disabledDates: [],
};

// Sunday (0) is blocked for all async providers — technicians unavailable
export const ASYNC_PROVIDER_DISABLED_DAYS = [0];

// TODO - move provider-specific DateReqs to review schema?
export const SpectrumDateRequirements: DateRequirements = {
  minDate: getDateOffsetByHours(72),
  maxDate: getDateOffsetByDays(29),
  disabledDates: SpectrumDisabledInstallationDates,
  disabledDaysOfWeek: ASYNC_PROVIDER_DISABLED_DAYS,
};

export const AttSelfInstallDateRequirements: DateRequirements = {
  minDate: getDateOffsetByHours(48),
  maxDate: getDateOffsetByDays(30),
  disabledDates: AttDisabledInstallationDates,
  disabledDaysOfWeek: ASYNC_PROVIDER_DISABLED_DAYS,
};

export const AttProInstallDateRequirements: DateRequirements = {
  minDate: getDateOffsetByHours(72),
  maxDate: getDateOffsetByDays(29),
  disabledDates: AttDisabledInstallationDates,
  disabledDaysOfWeek: ASYNC_PROVIDER_DISABLED_DAYS,
};

export const CoxDateRequirements: DateRequirements = {
  minDate: getDateOffsetByHours(48),
  maxDate: getDateOffsetByDays(30),
  disabledDates: CoxDisabledInstallationDates,
  disabledDaysOfWeek: ASYNC_PROVIDER_DISABLED_DAYS,
};

export const XfinityDateRequirements: DateRequirements = {
  minDate: getDateOffsetByHours(72),
  maxDate: getDateOffsetByDays(29),
  disabledDates: XfinityDisabledInstallationDates,
  disabledDaysOfWeek: ASYNC_PROVIDER_DISABLED_DAYS,
};

/**
 * Get default time window for provider and installation type
 * Returns string format for consistency (AT&T converts to array in transform)
 */
function getDefaultTimeWindow(
  provider: string,
  installationType: InstallationOptionsEnum
): string | undefined {
  switch (provider?.toLowerCase()) {
    case 'att':
      return installationType === InstallationOptionsEnum.self
        ? '8am to 6pm' // Self-install: wider window
        : AttInstallationWindows[0].value; // First available option
    case 'xfinity':
      return installationType === InstallationOptionsEnum.professional
        ? XfinityInstallationWindows[0].value // First available option
        : undefined; // No window for self-install
    case 'spectrum':
      return installationType === InstallationOptionsEnum.professional
        ? SpectrumInstallationWindows[0].value // First available option
        : undefined; // No window for self-install
    case 'cox':
      return installationType === InstallationOptionsEnum.professional
        ? CoxInstallationWindows[1].value
        : undefined; // No window for self-install
    default:
      return undefined;
  }
}

export function useGetEstimatedActivationDate(
  dateRequirements = DefaultDateRequirements
): ActivationDefaults {
  const { user, loading: userLoading } = useUserData();
  const { reservation, loading: reservationLoading } = useReservationAdapter();

  // In guest mode there's no authenticated user, but we still have the
  // reservation from context and can compute dates using minDate alone.
  const hasDataSource = Boolean(user) || Boolean(reservation);
  const loading = userLoading || reservationLoading;

  const moveDate = user?.currentMove?.date ?? formatDate(new Date());
  if (loading || !hasDataSource) {
    return {
      estimatedDate: null,
      dateRequirements,
      defaultTimeWindow: undefined,
      loading,
    };
  }

  const provider = reservation?.provider?.code || '';
  const installationType =
    (reservation?.offer?.options?.installationOptions
      ?.type as InstallationOptionsEnum) || InstallationOptionsEnum.self;

  switch (provider?.toLowerCase()) {
    case 'att':
      dateRequirements =
        installationType === InstallationOptionsEnum.self
          ? AttSelfInstallDateRequirements
          : AttProInstallDateRequirements;
      break;
    case 'spectrum':
      dateRequirements = SpectrumDateRequirements;
      break;
    case 'xfinity':
      dateRequirements = XfinityDateRequirements;
      break;
    case 'cox':
      dateRequirements = CoxDateRequirements;
      break;
    default:
      // use the passed-in requirements
      break;
  }

  const estimatedDate = getEstimatedActivationDate({
    moveDate,
    dateRequirements,
  });

  return {
    estimatedDate,
    dateRequirements,
    defaultTimeWindow: getDefaultTimeWindow(provider, installationType),
    loading,
  };
}

/**
 * Formats a date to MM/DD/YYYY using UTC.
 */
export function formatDate(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  return `${month}/${day}/${year}`;
}

/**
 * Parses a date string (MM/DD/YYYY or ISO format) into a Date object in UTC.
 */
export function parseDate(dateString: string): Date {
  const formats = ['MM/dd/yyyy', 'yyyy-MM-dd'];
  for (const formatStr of formats) {
    try {
      const parsed = parse(dateString, formatStr, new Date());
      if (!isNaN(parsed.getTime())) {
        return new Date(
          Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
        );
      }
    } catch {
      // Ignore invalid format and try the next one
    }
  }
  throw new Error(`Invalid date format: ${dateString}`);
}

/**
 * Compares two dates by year, month, and day.
 */
export function isEqualDate(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Checks if a date is in the list of disabled dates or falls on a disabled day of the week.
 */
export function isDateDisabled(
  date: Date,
  disabledDates: string[],
  disabledDaysOfWeek?: number[]
): boolean {
  if (disabledDaysOfWeek?.includes(date.getUTCDay())) {
    return true;
  }
  // Normalize the date with formatDate for consistent comparison
  return disabledDates.includes(formatDate(date));
}

/**
 * Finds the next available activation date.
 * If no valid activation date is found in the future, checks the past.
 */
export function getEstimatedActivationDate({
  moveDate,
  dateRequirements = SpectrumDateRequirements,
}: {
  moveDate: string;
  dateRequirements?: DateRequirements;
}): string | null {
  const { minDate, maxDate, disabledDates, disabledDaysOfWeek } =
    dateRequirements;

  const minDateParsed = parseDate(minDate);
  const maxDateParsed = parseDate(maxDate);
  const parsedMoveDate = parseDate(moveDate);
  let estimatedDate = parsedMoveDate;

  // Check from moveDate to maxDate (forward check)
  while (estimatedDate <= maxDateParsed) {
    if (
      estimatedDate >= minDateParsed &&
      !isDateDisabled(estimatedDate, disabledDates, disabledDaysOfWeek)
    ) {
      return formatDate(estimatedDate); // UTC-safe format
    }
    estimatedDate = addDays(estimatedDate, 1);
  }

  // Reset estimatedDate for backward check
  estimatedDate = parsedMoveDate;

  // Check from moveDate to minDate (backward check)
  while (estimatedDate >= minDateParsed) {
    if (
      estimatedDate <= maxDateParsed &&
      !isDateDisabled(estimatedDate, disabledDates, disabledDaysOfWeek)
    ) {
      return formatDate(estimatedDate); // UTC-safe format
    }
    estimatedDate = addDays(estimatedDate, -1);
  }

  return null; // No valid date found
}
